import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slide,
  IconButton,
  Stack,
  alpha,
  useTheme
} from '@mui/material';
import { Close as CloseIcon, RestartAlt as RestartAltIcon } from '@mui/icons-material';
import { updateUserBonus } from '../util/db';
import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { useAuth } from '../util/auth';
import { resetBonusTracking } from '../util/firebase';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ResetBonusModal({ open, onClose, bank, existingBonus, onReset }) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const isDark = theme.palette.mode === 'dark';
  const [isResetting, setIsResetting] = useState(false);
  const { user } = useAuth();

  const handleReset = async () => {
    try {
      setIsResetting(true);
      await resetBonusTracking(user.uid, bank.id);
      
      // Force a refresh of the parent component's data
      if (onReset) {
        await onReset();
      }
      
      enqueueSnackbar('Bonus tracking reset successfully', { variant: 'success' });
      onClose();
    } catch (error) {
      console.error('Reset failed:', error);
      enqueueSnackbar('Failed to reset tracking', { variant: 'error' });
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDark ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
          backgroundImage: 'none',
          boxShadow: `0 8px 32px ${isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.1)'}`,
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        Reset Bonus Tracking
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
            {bank?.name} - ${bank?.amount} Bonus
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Are you sure you want to reset tracking for this bonus? This will clear all progress.
          </Typography>
          <Stack spacing={1.5} sx={{ pl: 2 }}>
            <Typography color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              • Clear the start date
            </Typography>
            <Typography color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              • Remove the expected payout date
            </Typography>
            <Typography color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              • Reset the bonus status
            </Typography>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              bgcolor: isDark ? alpha(theme.palette.grey[100], 0.08) : alpha(theme.palette.grey[900], 0.08),
            },
          }}
          disabled={isResetting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleReset}
          startIcon={<RestartAltIcon />}
          sx={{
            px: 3,
            bgcolor: theme.palette.error.main,
            '&:hover': {
              bgcolor: theme.palette.error.dark,
            },
          }}
          disabled={isResetting}
        >
          {isResetting ? 'Resetting...' : 'Reset Tracking'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 